import React, { useState, useEffect, useCallback, useContext } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar, GridFooter } from "@mui/x-data-grid";
import {
  Grid,
  Box,
  Card as MuiCard,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Button,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { spacing } from "@mui/system";
import Notification from "../../components/Notification";
import ConfirmDialog from "../../components/ConfirmDialog";
import axios from "axios";
import ViewExciseClaimDialog from "../../components/ViewExciseClaimDialog";
import ClaimPayoutDetailsDialog from "../../components/ClaimPayoutDetailsDialog";
import DataGridExport from "../../components/DataGridExport";
import { GlobalContext } from "../../contexts/GlobalContext";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import moment from "moment";
import SelectWrapper from "../../components/SelectBoxDataGridCell";
import { useTranslation } from "react-i18next";
import {
  claimCodeEndsWithZero,
  getExciseClaimPayoutDateSortValue,
  resolveExciseClaimPayoutDate,
} from "../../utils/claimPayoutDateUtils";
import { formatDisplayDate } from "../../utils/dateFormatUtils";
import { useDataGridLocaleText } from "../../utils/useDataGridLocaleText";
import { translateExciseClaimStatus } from "../../utils/claimStatusUtils";

const Card = styled(MuiCard)(spacing);

const ListExciseClaims = (props) => {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const { customerId, customerNumber } = useContext(GlobalContext);
  const [activeTab, setActiveTab] = useState(0); // 0 for 'All Claims' tab
  const [selectedCountry, setSelectedCountry] = useState("any");
  const [selectedYear, setSelectedYear] = useState("any");
  const [selectedStartMonth, setSelectedStartMonth] = useState("any");
  const [selectedEndMonth, setSelectedEndMonth] = useState("any");
  const [customerPreFinanceData, setCustomerPreFinanceData] = useState([]);
  const [selectedClaimCount, setSelectedClaimCount] = useState(0);
  const [selectedClaimTotalLiters, setSelectedClaimTotalLiters] = useState(0);
  const [selectedClaimTotalAmountLocal, setSelectedClaimTotalAmountLocal] = useState(0);
  const [selectedClaimTotalAmountEUR, setSelectedClaimTotalAmountEUR] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getFilteredClaimsByTab = useCallback(
    (claims) => {
      switch (activeTab) {
        case 0: // All Claims
          return claims;
        case 1: // Pending Claims
          return claims.filter((claim) => claim.status === "pending");
        case 2: // Finished Claims
          return claims.filter((claim) => claim.status === "finished");
        case 3: // Pre-Financed Claims
          return claims.filter((claim) => claim.status === "preFinanced");
        case 4: // (Partly) Rejected Claims
          return claims.filter((claim) => claim.status === "rejected" || claim.status === "partly_rejected");
        default:
          return claims;
      }
    },
    [activeTab]
  );

  const handleCountryValue = (value, name) => {
    setSelectedCountry(value);
  };

  const handleYearValue = (value, name) => {
    setSelectedYear(value);
  };

  const handleStartMonthValue = (value, name) => {
    setSelectedStartMonth(value);
  };

  const handleEndMonthValue = (value, name) => {
    setSelectedEndMonth(value);
  };

  const handleResetFilter = async () => {
    setSelectedCountry("any");
    setSelectedYear("any");
    setSelectedStartMonth("any");
    setSelectedEndMonth("any");
    fetchAndFilterClaims();
  };

  function generateArrayOfYears() {
    var max = new Date().getFullYear() + 1;
    var min = max - 5;
    var years = [];
    for (var i = max; i >= min; i--) {
      years.push({ yearId: i, yearText: i });
    }
    return years;
  }

  let yearArr = generateArrayOfYears().reverse();

  const monthArr = [
    { monthId: 1, monthText: t("Jan") },
    { monthId: 2, monthText: t("Feb") },
    { monthId: 3, monthText: t("Mar") },
    { monthId: 4, monthText: t("Apr") },
    { monthId: 5, monthText: t("May") },
    { monthId: 6, monthText: t("Jun") },
    { monthId: 7, monthText: t("Jul") },
    { monthId: 8, monthText: t("Aug") },
    { monthId: 9, monthText: t("Sep") },
    { monthId: 10, monthText: t("Oct") },
    { monthId: 11, monthText: t("Nov") },
    { monthId: 12, monthText: t("Dec") },
  ];

  const fetchAndFilterClaims = useCallback(async () => {
    if (!customerId) {
      return;
    }
    setLoading(true);

    try {
      const response = await axios.get(`/api/v1/exciseClaimsByEntityId/${customerId}`);
      let filteredClaims = response.data;

      // Apply status logic based on claimCode and claimStateId
      filteredClaims = filteredClaims.map((claim) => {
        const claimCode = claim.claimCode;
        const lastTwoDigits = claimCode.slice(-2);

        if (lastTwoDigits === "00" || lastTwoDigits === "01") {
          if (claim.claimStateId === 4) {
            claim.status = "pending";
          } else if (claim.claimStateId === 16) {
            claim.status = "finished";
          }
        } else if (lastTwoDigits === "11") {
          claim.status = "preFinanced";
        }

        const paymentReceivedValue = claim.paymentReceivedDate ?? claim.PaymentReceivedDate;
        claim.paymentReceivedDate = paymentReceivedValue ? String(paymentReceivedValue).split("T")[0] : paymentReceivedValue;
        const paymentAmountValue = claim.paymentAmount ?? claim.PaymentAmount;
        claim.paymentAmount = paymentAmountValue;

        return claim;
      });

      // Apply filters
      if (selectedCountry !== "any") {
        filteredClaims = filteredClaims.filter((claim) => claim.countryId === selectedCountry);
      }
      if (selectedYear !== "any") {
        filteredClaims = filteredClaims.filter((claim) => claim.year === selectedYear);
      }
      if (selectedStartMonth !== "any") {
        filteredClaims = filteredClaims.filter((claim) => claim.startMonth === Number(selectedStartMonth));
      }
      if (selectedEndMonth !== "any") {
        filteredClaims = filteredClaims.filter((claim) => claim.endMonth === Number(selectedEndMonth));
      }
      // Apply tab-specific filtering
      filteredClaims = getFilteredClaimsByTab(filteredClaims);

      setTableData(filteredClaims);
    } catch (error) {
      console.error("Error fetching claims by entity ID:", error);
    } finally {
      setLoading(false);
    }
  }, [customerId, selectedCountry, selectedYear, selectedStartMonth, selectedEndMonth, getFilteredClaimsByTab]);

  useEffect(() => {
    if (customerId) {
      fetchAndFilterClaims();
    }
  }, [customerId, fetchAndFilterClaims]); // Add fetchAndFilterClaims to the dependency array

  const [countryData, setCountryData] = useState([]);
  const [currencyData, setCurrencyData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const [countryResponse, currencyResponse] = await Promise.all([axios.get("/api/v1/countries"), axios.get("/api/v1/currencies")]);
      setCountryData(countryResponse.data);
      setCurrencyData(currencyResponse.data);
    };
    fetchData();
  }, []);

  useEffect(() => {
    axios.get("/api/v1/preFinanceByEntityId/" + customerId).then((response) => setCustomerPreFinanceData(response.data));
  }, [customerId]);

  const nonRealDates = [
    "0000-00-00",
    "01-01-1900",
    "01-01-1970",
    "00-00-0000",
    "01-01-0001",
    "0001-01-01",
    "01-01-1",
    "0001-01-01T00:00:00",
    "1-1-1",
    "01-1-1",
    "1-01-1",
  ];

  const isValidDate = (dateString) => {
    // Return false for null, undefined, or empty string
    if (!dateString || typeof dateString !== "string") return false;

    // Normalize the date string by removing time component if present
    const normalizedDate = dateString.split("T")[0];

    // Check against known invalid dates
    if (nonRealDates.includes(normalizedDate)) return false;

    // Try parsing with moment using strict mode
    const date = moment(dateString, moment.ISO_8601, true);

    // Check if the date is valid and after 1970
    return date.isValid() && date.year() > 1970;
  };

  const columns = [
    {
      field: "claimCode",
      headerName: t("Claim Code"),
      flex: 0.5,
    },
    {
      field: "year",
      headerName: t("Year"),
      flex: 0.5,
    },
    {
      field: "startMonth",
      headerName: t("Start Month"),
      flex: 0.5,
      valueFormatter: (params) => params.value.toString().padStart(2, "0"),
    },
    {
      field: "endMonth",
      headerName: t("End Month"),
      flex: 0.5,
      valueFormatter: (params) => params.value.toString().padStart(2, "0"),
    },
    {
      field: "countryId",
      headerName: t("Country"),
      flex: 0.5,
      valueGetter: (params) => countryData.find((country) => country.countryId === params.row.countryId)?.countryCode || "",
    },
    {
      field: "status",
      headerName: t("Claim State"),
      flex: 0.5,
      valueGetter: (params) => {
        return params.row.status;
      },
      valueFormatter: (params) =>
        params.value ? translateExciseClaimStatus(params.value, t) : "",
    },
    {
      field: "claimDate",
      headerName: t("Claim Date"),
      flex: 0.5,
      valueGetter: (params) => {
        return {
          date: params.value,
          claimCode: params.row?.claimCode,
        };
      },
      valueFormatter: (params) => {
        const { date, claimCode } = params.value;

        // If no claimCode, return N/A
        if (!claimCode) return t("N/A");

        const lastTwoDigits = claimCode.slice(-2);

        // For claimCode ending in 11, always return N/A
        if (lastTwoDigits === "11") {
          return t("N/A");
        }

        // Check if date is valid using isValidDate function
        if (!isValidDate(date)) {
          return t("N/A");
        }

        // Format the valid date
        return moment(date).format("DD-MM-YYYY");
      },
    },
    {
      field: "reimbursementDate",
      headerName: t("Refund Date"),
      flex: 0.5,
      valueGetter: (params) => {
        return {
          date: params.value,
          claimCode: params.row?.claimCode,
        };
      },
      valueFormatter: (params) => {
        const { date, claimCode } = params.value;

        // If no claimCode, return N/A
        if (!claimCode) return t("N/A");

        const lastTwoDigits = claimCode.slice(-2);

        // For claimCode ending in 11, always return N/A
        if (lastTwoDigits === "11") {
          return t("N/A");
        }

        // Check if date is valid using isValidDate function
        if (!isValidDate(date)) {
          return t("N/A");
        }

        // Format the valid date
        return moment(date).format("DD-MM-YYYY");
      },
    },
    {
      field: "payDateSortable",
      headerName: t("Payout Date"),
      flex: 0.55,
      sortingOrder: ["asc", "desc"],
      valueGetter: (params) => getExciseClaimPayoutDateSortValue(params.row),
      sortComparator: (a, b) => a - b,
      renderCell: (params) => {
        const resolved = resolveExciseClaimPayoutDate(params.row);

        if (resolved.kind === "na") {
          const { claimCode } = params.row || {};

          if (claimCodeEndsWithZero(claimCode)) {
            const handleOpenPayoutDetails = (event) => {
              event.stopPropagation();
              setPayoutDetailsDialog({ isOpen: true, claim: params.row });
            };

            return (
              <Tooltip title={t("Click to enter payment received details")}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleOpenPayoutDetails}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleOpenPayoutDetails(event);
                    }
                  }}
                  tabIndex={0}
                  aria-label={t("Enter payment received details for {{code}}", { code: claimCode })}
                  sx={{
                    border: 0,
                    background: "none",
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    p: 0,
                    font: "inherit",
                  }}
                >
                  {t("N/A")}
                </Box>
              </Tooltip>
            );
          }

          return t("N/A");
        }

        if (resolved.kind === "preFinanced") {
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              {t("Pre-Financed")}
              <Tooltip
                title={t(
                  "Due to pre-financing, the payout date is related to the individual invoices. Please look at the individual invoices to see when the payment took place."
                )}
                enterTouchDelay={0}
                placement="top"
              >
                <InfoIcon sx={{ color: "#376FD0", marginLeft: 1, fontSize: 16, cursor: "pointer" }} />
              </Tooltip>
            </div>
          );
        }

        return formatDisplayDate(resolved.value);
      },
    },
    {
      field: "liters",
      headerName: t("Liters"),
      flex: 0.5,
      align: "right",
      renderCell: (params) => (
        <div style={{ textAlign: "right", paddingRight: "15px" }}>
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "currency",
      headerName: t("Currency"),
      flex: 0.5,
      valueGetter: (params) => {
        const selectedCountry = countryData.find((country) => country.countryId === params.row.countryId);
        const selectedCurrency = currencyData.find((currency) => currency.currencyId === selectedCountry?.currency);
        return selectedCurrency ? selectedCurrency.isoCode : "";
      },
    },
    {
      field: "claimAmountLocal",
      headerName: t("Amount Local"),
      flex: 0.5,
      align: "right",
      renderCell: (params) => (
        <div style={{ textAlign: "right", paddingRight: "15px" }}>
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "claimAmountEUR",
      headerName: t("Amount EUR"),
      flex: 0.5,
      align: "right",
      renderCell: (params) => (
        <div style={{ textAlign: "right", paddingRight: "15px" }}>
          {params.value.toLocaleString("nl-NL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      ),
    },
    {
      field: "preFinanceId",
      headerName: t("Pre-Financed"),
      flex: 0.4,
      renderCell: (params) => (params.value > 0 ? <CheckCircleOutlineIcon style={{ color: "green", margin: "0 auto" }} /> : ""),
    },
    {
      field: "preFinanceDate",
      headerName: t("Pre-Financed Date"),
      flex: 0.55,
      valueGetter: (params) => {
        if (params.row.preFinanceId > 0) {
          const found = customerPreFinanceData.find((item) => item.preFinanceId === params.row.preFinanceId);
          if (found && found.payedOut === 1) {
            return found ? moment(found.payoutDate).format("DD-MM-YYYY") : "";
          }
        }
        return "";
      },
      renderCell: (params) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: "15px" }}>{params.value}</div>
      ),
    },
    {
      field: "actions",
      headerName: t("View"),
      flex: 0.5,
      renderCell: (params) => {
        return (
          <Box>
            <IconButton
              onClick={(e) => {
                setViewExciseClaimDialog({
                  isOpen: true,
                  title: t("View Excise Claim"),
                  params: params.row,
                  countryData: countryData,
                  downloadFile: true,
                  custInfo: [{ customerId: customerId, customerNumber: customerNumber }],
                  subTitle: t("Are you sure you want to delete {{code}}?", { code: params.row.claimCode }),
                  onConfirm: () => {
                    onEditConfirmed(e, params.row);
                  },
                });
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });

  const [viewExciseClaimDialog, setViewExciseClaimDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    custInfo: [],
  });

  const [payoutDetailsDialog, setPayoutDetailsDialog] = useState({
    isOpen: false,
    claim: null,
  });

  function onEditConfirmed(e, params) {
    window.location.reload(false);

    setViewExciseClaimDialog({ ...viewExciseClaimDialog, isOpen: false });
  }

  const [pageSize, setPageSize] = useState(15);

  const handleSelectionChange = (newSelection) => {
    let selectedRowsAmountLocal = 0;
    let selectedRowsAmountEUR = 0;
    let selectedRowsLiters = 0;

    newSelection.forEach((rowId) => {
      const selectedRow = tableData.find((row) => row.claimId === rowId);
      if (selectedRow) {
        selectedRowsAmountLocal += selectedRow.claimAmountLocal;
        selectedRowsAmountEUR += selectedRow.claimAmountEUR;
        selectedRowsLiters += selectedRow.liters;
      }
    });

    setSelectedClaimCount(newSelection.length);
    setSelectedClaimTotalLiters(selectedRowsLiters);
    setSelectedClaimTotalAmountLocal(selectedRowsAmountLocal);
    setSelectedClaimTotalAmountEUR(selectedRowsAmountEUR);
  };

  const BulkEditFooter = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderTop: "1px solid #ccc",
          paddingTop: 10,
          paddingLeft: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Selected Claims")}:</div>
            <div style={{ marginBottom: 5 }}>{selectedClaimCount}</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Liters")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedClaimTotalLiters.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Amount Local")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedClaimTotalAmountLocal.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", marginLeft: 20 }}>
            <div style={{ fontWeight: "bold", marginBottom: 5 }}>{t("Total Amount EUR")}:</div>
            <div style={{ marginBottom: 5 }}>
              {selectedClaimTotalAmountEUR.toLocaleString("nl-NL", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>
        <div>
          <GridFooter
            style={{ borderTop: "none" }}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[15, 50, 100]}
          />
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <Helmet title={t("Claims")} />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item></Grid>
      </Grid>
      <Tabs value={activeTab} onChange={handleTabChange} aria-label={t("Claim Tabs")} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tab label={t("All Excise Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pending Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Finished Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("Pre-Financed Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
        <Tab label={t("(Partly) Rejected Claims")} sx={{ typography: "h6", fontWeight: "bold" }} />
      </Tabs>
      <Card mb={6}>
        <Notification notify={notify} setNotify={setNotify} />
        <Divider />
        <Grid container spacing={2} mt={0}>
          <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
          <ViewExciseClaimDialog
            viewExciseClaimDialog={viewExciseClaimDialog}
            setViewExciseClaimDialog={setViewExciseClaimDialog}
            refreshTable={fetchAndFilterClaims}
          />
          <ClaimPayoutDetailsDialog
            dialogState={payoutDetailsDialog}
            setDialogState={setPayoutDetailsDialog}
            onSaved={fetchAndFilterClaims}
            claimType="excise"
          />
          <Grid container xs={12} spacing={3} sx={{ pr: 2, mt: 2, ml: 2, mb: -2 }}>
            <Grid item xs={2}>
              <SelectWrapper
                name="countryId"
                labelColor="#f64435"
                label={t("Country")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(
                      JSON.parse(
                        JSON.stringify(countryData.filter((country) => Number(country.countryId) >= 50)).split('"countryId":').join('"key":')
                      )
                    )
                      .split('"countryCode":')
                      .join('"value":')
                  ),
                ]}
                value={selectedCountry}
                handleChange={handleCountryValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="year"
                label={t("Year")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(yearArr).split('"yearId":').join('"key":')))
                      .split('"yearText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedYear}
                handleChange={handleYearValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="startMonth"
                label={t("Start month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedStartMonth}
                handleChange={handleStartMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <SelectWrapper
                name="endMonth"
                label={t("End month")}
                options={[
                  { key: "any", value: t("Any") },
                  ...JSON.parse(
                    JSON.stringify(JSON.parse(JSON.stringify(monthArr).split('"monthId":').join('"key":')))
                      .split('"monthText":')
                      .join('"value":')
                  ),
                ]}
                value={selectedEndMonth}
                handleChange={handleEndMonthValue}
              />
            </Grid>
            <Grid item xs={2}>
              <Button onClick={handleResetFilter} sx={{ mt: 4, ml: 4, pl: 6, pr: 6 }} variant="outlined">
                {t("Reset")}
              </Button>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <DataGridExport
                tableData={tableData}
                columns={columns}
                exportType="exciseClaims"
                customerId={customerId}
                customerNumber={customerNumber}
              />
            </div>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "600px",
                }}
              >
                <Typography sx={{ mr: 6, fontSize: 20, color: "text.primary" }}>{t("Loading")}</Typography>
                <CircularProgress />
              </div>
            ) : (
              countryData.length > 0 && (
                <DataGrid
                  checkboxSelection
                  getRowId={(row) => row.claimId}
                  localeText={dataGridLocaleText}
                  disableSelectionOnClick
                  disableColumnSelector
                  components={{
                    NoRowsOverlay: () => (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        {t("No Claims Found")}
                      </Stack>
                    ),
                    Toolbar: GridToolbar,
                    Footer: BulkEditFooter,
                  }}
                  density="compact"
                  componentsProps={{
                    toolbar: {
                      csvOptions: { disableToolbarButton: true },
                      printOptions: { disableToolbarButton: true },
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 250 },
                    },
                  }}
                  onSelectionModelChange={handleSelectionChange}
                  rows={tableData}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  rowsPerPageOptions={[15, 50, 100]}
                  sx={{
                    height: 667,
                    width: "100%",
                    borderRadius: 1,
                    backgroundColor: "background.paper",
                    boxShadow: 2,
                    "& .MuiDataGrid-cell:hover": {
                      color: "primary.main",
                    },
                    "& .MuiTablePagination-root": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-displayedRows": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-select": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      color: "text.primary",
                    },
                    "& .MuiTablePagination-actions button": {
                      color: "text.primary",
                    },
                    marginTop: 0,
                  }}
                />
              )
            )}
          </Grid>
        </Grid>
      </Card>
    </React.Fragment>
  );
};

export default ListExciseClaims;
